import { useContext, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { isPlanError } from '../../components/plan/planErrors';
import { UpsellFromError } from '../../components/plan/Upsell';
import { QuotaContext } from '../../components/quota/quotaContext';
import type { ApiError } from '../../lib/apiClient';
import type { AssistantActionCard } from '../../types/api';
import { cardFailureMessage, cardWording, costLine, outcomeLink, outcomeText } from './actionCards';

export type CardCall = (actionId: string) => Promise<AssistantActionCard>;

interface AssistantActionCardsProps {
  cards: readonly AssistantActionCard[];
  /** Absent while the reply is still being written: the cards are read-only then. */
  onConfirm?: CardCall;
  onCancel?: CardCall;
  /** Hands a sentence to the conversation's live region. */
  onAnnounce?: (text: string) => void;
}

/**
 * What the assistant proposes and only the user can set off (tiers C and O):
 * one card each under its reply. Nothing has happened until Confirm.
 */
export function AssistantActionCards({
  cards,
  onConfirm,
  onCancel,
  onAnnounce,
}: AssistantActionCardsProps) {
  if (cards.length === 0) return null;
  return (
    <ul className="assistant-cards" aria-label="Waiting on you">
      {cards.map((card) => (
        <li key={card.action_id}>
          <ActionCard
            card={card}
            onConfirm={onConfirm}
            onCancel={onCancel}
            onAnnounce={onAnnounce}
          />
        </li>
      ))}
    </ul>
  );
}

interface ActionCardProps {
  card: AssistantActionCard;
  onConfirm?: CardCall;
  onCancel?: CardCall;
  onAnnounce?: (text: string) => void;
}

function ActionCard({ card, onConfirm, onCancel, onAnnounce }: ActionCardProps) {
  const quotaContext = useContext(QuotaContext);
  const [busy, setBusy] = useState<'confirm' | 'cancel' | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A 402: the card's action is not on the plan (publish, autopilot, a locked brand).
  const [planError, setPlanError] = useState<ApiError | null>(null);
  const [expiredHere, setExpiredHere] = useState(false);
  const [focusTick, setFocusTick] = useState(0);
  const outcomeRef = useRef<HTMLParagraphElement>(null);

  // A pending card past its window reads as expired before the server says so.
  const expiresAt = card.status === 'pending' ? Date.parse(card.expires_at) : Number.NaN;
  useEffect(() => {
    if (!Number.isFinite(expiresAt)) return;
    const timer = window.setTimeout(
      () => setExpiredHere(true),
      Math.min(Math.max(0, expiresAt - Date.now()), 2_147_000_000),
    );
    return () => window.clearTimeout(timer);
  }, [expiresAt]);

  // After a confirm or cancel the buttons are gone: focus goes to the outcome.
  useEffect(() => {
    if (focusTick > 0) outcomeRef.current?.focus();
  }, [focusTick]);

  const status = card.status === 'pending' && expiredHere ? 'expired' : card.status;
  const wording = cardWording(card);
  const cost = costLine(card, quotaContext?.quota ?? null);
  const isOpen = status === 'pending';
  const isWorking = busy === 'confirm' || status === 'executing';
  const canAct = isOpen && onConfirm !== undefined && onCancel !== undefined;
  const link = status === 'done' ? outcomeLink(card) : null;

  async function act(kind: 'confirm' | 'cancel') {
    const call = kind === 'confirm' ? onConfirm : onCancel;
    if (!call || busy !== null) return;
    setBusy(kind);
    setError(null);
    setPlanError(null);
    try {
      const next = await call(card.action_id);
      // A C card spent (or tried to): the counter re-reads either way.
      if (kind === 'confirm' && card.tier === 'C') quotaContext?.refresh();
      onAnnounce?.(outcomeText(next, next.status));
      setFocusTick((tick) => tick + 1);
    } catch (caught) {
      const message = cardFailureMessage(caught);
      if (isPlanError(caught)) setPlanError(caught);
      else setError(message);
      onAnnounce?.(message);
    } finally {
      setBusy(null);
    }
  }

  const cardClass = [
    'assistant-card',
    card.tier === 'O' ? 'assistant-card--outward' : 'assistant-card--costed',
    `assistant-card--${status}`,
  ].join(' ');

  const outcomeTone =
    status === 'done'
      ? 'assistant-card__outcome--done'
      : status === 'failed'
        ? 'assistant-card__outcome--failed'
        : 'assistant-card__outcome--quiet';

  return (
    <article className={cardClass} aria-busy={isWorking || undefined}>
      <p className="assistant-card__marker">{wording.marker}</p>
      <p className="assistant-card__summary">{card.summary}</p>
      {cost && (isOpen || status === 'executing') ? (
        <p className="assistant-card__cost">{cost}</p>
      ) : null}

      {canAct || (isOpen && busy !== null) ? (
        <div className="assistant-card__actions">
          <button
            className={
              card.tool.startsWith('delete_')
                ? 'button button--danger assistant-card__confirm'
                : 'button button--primary assistant-card__confirm'
            }
            type="button"
            // aria-disabled rather than disabled, so focus stays put meanwhile.
            aria-disabled={busy !== null}
            onClick={() => void act('confirm')}
          >
            {isWorking ? 'Working' : wording.confirm}
          </button>
          <button
            className="button button--ghost assistant-card__cancel"
            type="button"
            aria-disabled={busy !== null}
            onClick={() => void act('cancel')}
          >
            {busy === 'cancel' ? 'Cancelling' : 'Cancel'}
          </button>
        </div>
      ) : isOpen ? (
        <p className="assistant-card__note">You can confirm this once the reply is in.</p>
      ) : (
        <p className={`assistant-card__outcome ${outcomeTone}`} ref={outcomeRef} tabIndex={-1}>
          <span>{outcomeText(card, status)}</span>
          {link ? (
            <>
              {' '}
              <Link className="assistant-card__open" to={link}>
                Open
              </Link>
            </>
          ) : null}
        </p>
      )}

      {error ? <p className="assistant-card__error">{error}</p> : null}
      <UpsellFromError error={planError} />
    </article>
  );
}
